import { watch, onScopeDispose } from 'vue';
import type { Ref } from 'vue';

/**
 * Trava o scroll do body via `position: fixed` (compensando o scroll atual
 * em `top`, restaurado no destrave) — ao contrário de `overflow: hidden`,
 * isso bloqueia scroll por TOQUE no iOS Safari também.
 *
 * Separado de `useBodyScrollLock` (usado pelo menu mobile, cashback e pelos
 * modais do `Global.vue`) de propósito: aquele composable evita
 * `position: fixed` porque quebra o comportamento de esconder a barra de
 * endereço do Safari iOS pro menu mobile, que fica aberto mais tempo e é
 * navegado com mais frequência. Aqui é só pra modais pontuais (depósito,
 * cadastro de conta bancária) — bug real reportado: dava pra rolar o
 * conteúdo atrás desses modais no mobile.
 *
 * Mesmo contador de referência em nível de módulo (vários desses modais
 * podem abrir em sequência rápida), mas em contagem própria — não
 * compartilhada com `useBodyScrollLock`.
 */
let lockCount = 0;
let savedScrollY = 0;

function apply() {
  if (!import.meta.client) {
    return;
  }

  if (lockCount > 0) {
    if (document.body.style.position === 'fixed') {
      return;
    }

    savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  } else {
    if (document.body.style.position !== 'fixed') {
      return;
    }

    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');
    window.scrollTo(0, savedScrollY);
  }
}

function acquire() {
  lockCount++;
  apply();
}

function release() {
  if (lockCount === 0) {
    return;
  }

  lockCount--;
  apply();
}

/**
 * Observa um booleano reativo e trava/destrava o body conforme ele abre e
 * fecha. Libera sozinho ao desmontar o componente (evita lock preso se o
 * modal sumir sem transicionar `false`).
 */
export function useModalScrollLock(source: Ref<boolean>) {
  let held = false;

  function set(active: boolean) {
    if (active && !held) {
      held = true;
      acquire();
    } else if (!active && held) {
      held = false;
      release();
    }
  }

  watch(source, set, { immediate: true });

  onScopeDispose(() => set(false));
}
